import { AfterViewInit, Component, DestroyRef, inject, Input, OnInit } from "@angular/core";
import { ModalService } from "@core-services/modal.service";
import { MasterdataService } from "@core-services/masterdata.service";
import { TranslationService } from "@core-services/translation.service";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { Router } from "@angular/router";
import { TransactionListDetailComponent } from "../transaction-list-detail.component";
import { Contact, Fiat, ITradeFee, ITradeTax } from "@models";
import { TradableWallet } from "@models/interfaces/tradable_wallet";
import { empty, isEmail } from "@helpers/utils";
import {
	CryptoTransaction,
	FiatTransaction,
	IFeeTransparencyTX,
	TradableTransaction,
	Transaction
} from "@models/transaction";
import { AccountService } from "@modules/account/services/account.service";
import { FiatService } from "@main-services/currency";
import { ContactService } from "@main-services/contact.service";
import { PayAccountService } from "@main-services/pay_account.service";
import { ContactEditComponent } from "@modules/main/components/contact/contact-edit-modal/contact-edit.component";
import { BehaviorSubject, EMPTY, Observable } from "rxjs";
import { finalize, map } from "rxjs/operators";
import { LeveragedTokensFacade } from "@modules/main/components/leveraged-tokens/state/leveraged-tokens.facade";
import { isEarnOnFiatTagComparisonHelper } from "@modules/transaction/transaction.utils";
import { AssetGroupName } from "@modules/+new/shared/const/enums/asset-group-name.enum";
import { TaxesRouteNames, TaxesTransactionTypes } from "@modules/taxes/domain/entities/taxes.model";
import { trigger } from "@angular/animations";
import { fadeIn } from "../../../../../../animations/animations";
import {
	CapitalGainsTaxesModel,
	TransactionTaxesFacade,
	TransactionTaxesModel
} from "@modules/transaction/components/transaction-list/transaction-list-detail/transaction-taxes.facade";
import { TaxesTrackerFacade } from "@modules/taxes/domain/+state/taxes-tracker-facade.service";
import { DepositCellEvent } from "@modules/taxes/features/taxes-multi-declaration/deposit-list/deposit-cell/deposit-cell.component";
import { TaxesMultiDeclarationFacade } from "@modules/taxes/domain/+state/taxes-multi-declaration.facade";
import { TaxesUndeclaredTrxFacade } from "@modules/taxes/domain/+state/taxes-undeclared-trx.facade";
import { TaxesMultiDeclarationStateService } from "@modules/taxes/domain/+state/taxes-multi-declaration.state.service";
import { TAXES_TRX_DETAIL_PROMPT_CLICKED } from "@modules/taxes/domain/entities/taxes-tracking.model";
import { ROUTER_OUTLET_NAME } from "@modules/+new/shared/universal-modal/entities/magic";
import { TAXES } from "@helpers/constants";
import { TaxesQueryParamName } from "@modules/taxes/domain/entities/taxes-navigation.model";
import { TransactionWrapperModalComponent } from "@modules/transaction/modals/transaction-wrapper-modal/transaction-wrapper-modal.component";
import { TaxesRemote } from "@modules/taxes/domain/infrastructure/taxes.remote";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import {
	statusColorMapping,
	statusTranslationKeyMapping,
	TransactionInfo,
	TransactionInfoStatus
} from "@modules/taxes/domain/entities/transaction-info.model";
import { ProfileModel } from "@modules/main/components/profile/new-profile/entities/profile-model";
import { NewProfileFacade } from "@modules/main/components/profile/new-profile/state/new-profile.facade";

interface ITaxRefundRelatedTx {
	enabled: boolean;
	fiatWalletId?: string;
	relatedTxId?: string;
	type?: string;
}

interface ITradeFeeProps extends ITradeFee {
	is_fee_transparent: boolean;
}

@Component({
	selector: "transaction-list-detail-template",
	templateUrl: "./transaction-list-detail-template.component.html",
	styleUrls: ["./transaction-list-detail-template.component.scss"],
	providers: [
		TransactionTaxesFacade,
		TaxesTrackerFacade,
		TaxesMultiDeclarationFacade,
		TaxesMultiDeclarationStateService,
		TaxesUndeclaredTrxFacade
	],
	animations: [trigger("fadeIn", fadeIn)],
	standalone: false
})
export class TransactionListDetailTemplateComponent
	extends TransactionListDetailComponent
	implements OnInit, AfterViewInit
{
	@Input() getFrom: string | undefined = undefined;
	@Input() getTo: string | undefined = undefined;
	@Input() networkName: string | undefined = undefined;

	public leveragedTokenBaseSymbol: Observable<string> = EMPTY;
	public isCashPlus: boolean = false;

	private isLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
	public showTaxRefundRelatedTx$ = new BehaviorSubject<ITaxRefundRelatedTx>({ enabled: false });
	private taxesRemote = inject(TaxesRemote);
	public isIndexClaim: boolean = false;
	public transactionInfoStatus: {
		status: string;
		color: string;
		wallet_host: string;
		to: string;
		destination_tag_type?: string;
		destination_tag_value?: string;
		walletAddress: string;
	} = {
		status: "",
		color: "",
		wallet_host: "",
		to: "",
		walletAddress: ""
	};
	public shouldDisplayNewCryptoTransactionData: boolean = false;
	public shouldDisplayLeveragedTransaction: boolean = false;
	public shouldDisplayTo: boolean = false;
	public displayRejectedInfoTooltip: boolean = false;
	public fromField: string;
	public mergerUnitPrice: number = 0;
	private profileName: string;

	public get isLoading(): Observable<boolean> {
		return this.isLoading$.asObservable();
	}
	public readonly trxTaxesInfoVM$: Observable<TransactionTaxesModel> = this.transactionTaxesFacade.taxesInfoVM$;
	public capitalGainsTaxes$: Observable<CapitalGainsTaxesModel> = EMPTY;

	public readonly taxesTrxTypes = TaxesTransactionTypes;

	public fee: Partial<ITradeFeeProps> = null;
	public feeInSwapTrade: Partial<ITradeFeeProps> = null;
	public feeInCardTx: Partial<IFeeTransparencyTX> = null;

	// fee transparency flag and crypto type only flag
	isTradeFeeAvailableForCrypto$: Observable<boolean> = this.launchDarkly
		.isActive$(LD_FLAG.FEE_TRANSPARENCY_CRYPTO_ONLY)
		.pipe(
			map((flag) => {
				const trade = this.transaction.getTrade();
				const is_fee_transparent = trade.getIsFeeTransparent();

				// hide fee for withdrawal, deposit, send
				if (
					flag &&
					(this.transaction.isType("withdrawal") ||
						this.transaction.isType("deposit") ||
						this.transaction.isType("send"))
				) {
					return false;
				}

				// check is fee transparent
				if (flag && is_fee_transparent && Object.entries(trade.getFee()).length > 0) {
					this.fee = { ...trade.getFee(), is_fee_transparent };

					return true;
				}

				//check is fee transparent for swap trade
				if (
					flag &&
					trade.getRelatedSwapTrade()?.getFee() &&
					trade.getRelatedSwapTrade()?.getIsFeeTransparent()
				) {
					const fee = trade?.getRelatedSwapTrade()?.getFee();
					const isFeeEmpty = !fee || Object.keys(fee).length === 0;

					this.feeInSwapTrade = {
						fee_amount_in_fiat: isFeeEmpty ? "0" : fee.fee_amount_in_fiat,
						fee_percentage: isFeeEmpty ? 0 : fee.fee_percentage,
						is_fee_transparent: trade?.getRelatedSwapTrade()?.getIsFeeTransparent()
					};

					return true;
				}

				// check is fee transparent for card transactions
				if (flag && this.transaction.isType("withdrawal")) {
					this.feeInCardTx = this.transaction.getCardTxFeeTransparency();

					return true;
				}

				return false;
			})
		);

	public constructor(
		protected modalService: ModalService,
		protected tradableWalletListService: TradableWalletListService,
		protected tradableListService: TradableListService,
		protected accountService: AccountService,
		protected fiatWalletService: FiatWalletService,
		protected fiatService: FiatService,
		protected contactService: ContactService,
		protected payaccountService: PayAccountService,
		protected translationService: TranslationService,
		protected masterdataService: MasterdataService,
		private router: Router,
		private readonly leveragedTokensFacade: LeveragedTokensFacade,
		private readonly transactionTaxesFacade: TransactionTaxesFacade,
		private readonly taxesTrackerFacade: TaxesTrackerFacade,
		private readonly launchDarkly: LaunchDarklyService,
		private readonly destroyRef: DestroyRef,
		private readonly profileFacade: NewProfileFacade
	) {
		super(
			modalService,
			tradableWalletListService,
			tradableListService,
			fiatWalletService,
			fiatService,
			contactService,
			payaccountService,
			masterdataService
		);
	}

	public ngOnInit(): void {
		this.profileFacade.getProfile$().subscribe(({ fullName }: ProfileModel) => (this.profileName = fullName));

		if (this.isLeveragedTx()) {
			this.leveragedTokenBaseSymbol = this.leveragedTokensFacade
				.fetchLeverageTokenInfo$(this.tradable.getId())
				.pipe(map((info) => info.attributes.base_asset_symbol));
		}

		this.isCashPlus = this.isCashPlusAsset();

		// Taxes TRX Info for specific transactions Types
		if (this.isTrxTaxable(this.transaction)) {
			this.transactionTaxesFacade.getTaxesTransactionInfo(this.transaction.getId());
		}

		// set tax refund related trade flag
		this.showTaxRefundRelatedTx$.next(this.showTaxRefundRelatedTx());

		// Avoid redirection to Wallet if it's Index Claim Trx
		this.isIndexClaim = this.transaction
			.getTransactionTags()
			.map((tag) => tag.attributes.short_name)
			.includes("index.claim");

		// Get data for Capital Gains Taxes
		this.getCapitalGainModel();

		this.shouldDisplayNewCryptoTransactionData =
			(this.transaction instanceof CryptoTransaction || this.transaction instanceof FiatTransaction) &&
			(this.transactionType.toLocaleLowerCase() === "deposit" ||
				this.transactionType.toLocaleLowerCase() === "withdrawal");

		if (this.shouldDisplayNewCryptoTransactionData) {
			this.handleNewTransactionStatus();
		} else {
			this.isLoading$.next(false);
		}

		if (this.isTrxCryptoMerger()) {
			this.transactionInfoStatus.status = this.transaction.getStatus();
			this.mergerUnitPrice = this.calculateMergerUnitPrice();
		}

		if (this.isTrxVisionFee()) {
			this.transaction.setRecipient("Bitpanda");
		}
	}

	public isEarnOnFiatElement(transaction: Transaction): boolean {
		const trade = transaction?.getTrade();
		return isEarnOnFiatTagComparisonHelper(trade?.getTags());
	}

	public isTrxTaxable(transaction: Transaction): boolean {
		const type = transaction.getType().toLowerCase();
		const tags = this.transaction.getTransactionTags();
		const invalidTags = ["reward", "instant_trade_bonus"];

		if (transaction instanceof CryptoTransaction) {
			if (type === "deposit" || (type === "transfer" && tags.length === 0)) {
				return true;
			}
			if (type === "transfer" && invalidTags.some((tag) => tags[0].attributes.short_name === tag)) {
				return false;
			}
		}
		return false;
	}

	public get transactionStatus(): string {
		if (this.transaction.getStatus() === "processing") {
			return "pending";
		}

		if (this.transaction) {
			return this.transaction.getStatus();
		}

		return "";
	}

	public getTax(): Partial<ITradeTax> {
		return this.transaction.getTrade()?.getTax();
	}

	public getCapitalGainModel(): void {
		const isFiatTrx = this.transaction.getFiatId() !== "";
		this.capitalGainsTaxes$ = this.transactionTaxesFacade.getCapitalGainsTax$(this.transaction.getId(), isFiatTrx);
	}

	public get transactionDate(): Date {
		return this.transaction.getLastChanged().date();
	}

	public get txPurposeText(): string {
		if (this.transaction instanceof TradableTransaction) {
			return this.transaction.getPurposeText();
		}

		if (this.transaction instanceof FiatTransaction) {
			return this.transaction.getPaymentReference();
		}

		return "";
	}

	public get referenceLocaKey(): string {
		if (this.transaction) {
			return this.isFiatSend() ? "send_fiat_payment_ref" : "tx_details_reference";
		}
		return "";
	}

	public get tradeMessage(): string {
		if (this.isTrxCryptoMerger()) {
			return "traded_tx_item";
		}

		if (this.transaction.hasTrade()) {
			return this.transaction.getType() === "buy" ? "bought_tx_item" : "sold_tx_item";
		}

		if (this.transaction.isWithdrawal() && this.transaction.isOutgoing()) {
			return "sent_tx_item";
		}

		if (this.isFiatWithdrawal()) {
			return "withdrew_tx_item";
		}

		if (this.isFiatDeposit()) {
			return "deposited_tx_item";
		}

		if (this.isExternalCryptoDepositWithFees()) {
			return "crypto_deposit_tx_item";
		}

		return "";
	}

	public get transactionType(): string {
		if (this.transaction.hasTrade() && this.transaction.getTrade().getIsSwap()) {
			return "swap";
		}
		if (this.isVoucherTx()) {
			return "voucher";
		}
		if (this.transaction.isRefund()) {
			return "refund";
		}
		if (this.transaction.isSavings || (this.transaction.hasTrade() && this.transaction.getTrade().getIsSavings())) {
			return "savings";
		}
		if (this.isTrxCryptoMerger()) {
			return "merger_crypto";
		}
		return this.transaction.getType();
	}

	public ngAfterViewInit(): void {
		if (this.transaction.hasDividendId()) {
			this.dividendAssetName = this.tradableListService
				.getItemById(this.transaction.getDividendAssetId())
				.getName();
		}
	}

	public goToTaxInfo(): void {
		const urlTree = this.router.createUrlTree(
			[{ outlets: { [ROUTER_OUTLET_NAME]: [TAXES, TaxesRouteNames.SuccessTaxInfo] } }],
			{ queryParams: { [TaxesQueryParamName.IsRefund]: false } }
		);
		this.closeEmitter.emit(true);
		this.router.navigateByUrl(urlTree);
	}

	public showPurposeText(): boolean {
		if (this.transaction instanceof TradableTransaction) {
			return this.txPurposeText.length > 0;
		}

		if (this.isFiatTransaction()) {
			return this.isFiatSend();
		}

		return false;
	}

	public canNavigateToWallet(isFromWallet: boolean = true, isIndexClaim: boolean): boolean {
		if (isIndexClaim) {
			return false;
		}
		if (isFromWallet) {
			return !this.isDeletedWallet() && this.enableFromWalletLink();
		}
		return !this.isDeletedWallet(false) && this.isClickable();
	}

	public showFee(): boolean {
		return (
			(this.transaction.isWithdrawal() && !this.isTrxCryptoMerger()) ||
			this.isFiatDeposit() ||
			this.isFiatWithdrawal() ||
			this.isExternalCryptoDepositWithFees()
		);
	}

	public hasExternalTxId(): boolean {
		if (this.transaction instanceof TradableTransaction) {
			return this.transaction.isExternal();
		}
		return false;
	}

	public contact(): Contact {
		if (this.transaction instanceof CryptoTransaction) {
			return this.contactService.getItemById(this.transaction.getAddressbookId()) || new Contact();
		}
		return null;
	}

	public getBestTxFiat(): Fiat {
		return this.transaction.getIsBfc() ? this.getBfcTradeFiat() : this.txFiat;
	}

	public showDetailsTopBorder(): boolean {
		return !this.showDetailSlate() && !this.transaction.hasDebitCardRelatedTx();
	}

	private showTaxRefundRelatedTx(): ITaxRefundRelatedTx {
		//both conditions are needed to show the tax refund related trade
		const fiatWalletId = this.transaction["fiat_wallet_id"];
		const relatedTxId = this.transaction["related_fiat_wallet_transaction_id"];
		const isRefund = this.transaction.isRefund() || this.transaction.getType() === "sell";
		const type = this.transaction.getType();

		if (fiatWalletId && relatedTxId && isRefund) {
			return { enabled: true, fiatWalletId, relatedTxId, type };
		}

		return { enabled: false };
	}

	public openTaxRefundRelatedTx(fiatWalletId: string, relatedTxId: string): void {
		this.taxesRemote.getTaxRefundRelatedTX$(fiatWalletId, relatedTxId).subscribe({
			next: (transaction) => {
				this.modalService
					.create(TransactionWrapperModalComponent, {
						transaction,
						canOpen: false,
						closeAllPopups: true
					})
					.present();
			}
		});
	}

	public showDetailSlate(): boolean {
		if (this.isTrxCryptoMerger()) {
			return true;
		}

		if (this.isFiatDebitCardTx()) {
			return false;
		}
		if (this.transaction instanceof TradableTransaction) {
			return (
				this.transaction.hasTrade() ||
				(this.transaction.hasFees() && !this.transaction.isStorageFee && !this.transaction.getIsBfc())
			);
		}
		return (
			this.transaction.hasTrade() ||
			((this.isFiatWithdrawal() || this.isFiatDeposit()) && +(<FiatTransaction>this.transaction).getFee() > 0)
		);
	}

	public enableFromWalletLink(): boolean {
		return (
			!this.isVoucherTx() &&
			this.transaction.getType() !== "deposit" &&
			!this.getGiveawayReward() &&
			!this.transaction.isRefund() &&
			!this.isOldGiveaway() &&
			!this.isDeletedWallet(true) &&
			this.transaction.getFrom() !== "Bitpanda"
		);
	}

	public isDeletedWallet(isFromWallet: boolean = true): boolean {
		if (this.isInternal && !this.isTrxMarginTradingClose()) {
			// If we are on a wallet's page, the 'this.assetWallet' is always the opened wallet
			// So only the other wallet has to be checked
			if (this.isCurrentWalletPage()) {
				return this.isCurrentWalletChecked(isFromWallet) ? this.relatedTradableWallet?.isDeleted() : false;
			}
			return this.isCurrentWalletChecked(isFromWallet)
				? this.relatedTradableWallet?.isDeleted()
				: this.tradableWallet.isDeleted();
		}
		const wallet = isFromWallet ? this.getFromWallet() : this.getToWallet();
		return wallet instanceof TradableWallet && wallet.isDeleted();
	}

	// Checks if the currently inspected AseetWallet's page is open
	public isCurrentWalletPage(): boolean {
		return window.location.pathname.includes(this.tradableWallet.getId());
	}

	// Checks if the currently inspected AseetWallet is the one the transaction belongs to
	public isCurrentWalletChecked(isFromWallet: boolean): boolean {
		return (
			(this.transaction.getInOrOut() === "incoming" && isFromWallet) ||
			(this.transaction.getInOrOut() === "outgoing" && !isFromWallet)
		);
	}

	public goToWallet(isOutgoingWallet: boolean = true): void {
		if (this.canOpenWallet(isOutgoingWallet)) {
			return;
		}

		this.closeEmitter.emit(true);

		let walletUrl: string = "";

		const hasTradableWallet: boolean = !empty(this.tradableWallet); // E.g. in case of dividends tradable wallet is undefined
		const hasFiatWallet: boolean = !empty(this.fiatWallet); // E.g. in case of incoming crypto from another user, Fiat wallet is undefined

		const fiatWalletUrl = hasFiatWallet ? this.fiatWallet.getBaseUrl() + this.fiatWallet.getId() : "";
		const tradableWalletUrl = hasTradableWallet
			? this.tradableWallet.getBaseUrl() + this.tradableWallet.getId()
			: "";

		if (
			this.transaction.getType() === "transfer" &&
			((!isOutgoingWallet && this.transaction.isOutgoing()) ||
				(isOutgoingWallet && !this.transaction.isOutgoing()))
		) {
			this.openContacts(); // Opens contact page
			return;
		}

		if (this.isInternal) {
			if (this.isCurrentWalletPage() || this.isTrxCryptoMerger()) {
				if (this.isCurrentWalletChecked(isOutgoingWallet) && !this.relatedTradableWallet.isDeleted()) {
					walletUrl = this.relatedTradableWallet.getId();
				} else {
					walletUrl = this.isCurrentWalletChecked(isOutgoingWallet)
						? this.relatedTradableWallet.getId()
						: this.tradableWallet.getId();
				}
			}
			this.router.navigate(["wallet", walletUrl]);
		} else {
			// In case of an internal_transfer it has to be checked which AssetWallet is clicked, and navigate to the correct one
			if (
				this.transaction.getType() === "refund" ||
				this.transactionType === "deposit" ||
				this.transactionType === "withdrawal"
			) {
				walletUrl = this.isFiatTransaction() ? fiatWalletUrl : tradableWalletUrl;
			}

			if (
				this.transaction.getType() === "transfer" &&
				!this.transaction.isOutgoing() &&
				this.isFiatTransaction() &&
				!isOutgoingWallet
			) {
				walletUrl = fiatWalletUrl;
			}

			// Wallet-handling mostly for trades and wallet-to-wallet
			if (this.transaction.getType() === "sell") {
				walletUrl = isOutgoingWallet ? tradableWalletUrl : fiatWalletUrl;
			} else if (
				this.transaction.getType() === "buy" ||
				(this.transaction.getType() === "transfer" && !this.transaction.isOutgoing())
			) {
				// Handle case of incoming "transfer" with no tradable wallet (e.g. dividends)
				if (!hasTradableWallet && !isOutgoingWallet) {
					walletUrl = fiatWalletUrl;
				} else {
					walletUrl = isOutgoingWallet ? fiatWalletUrl : tradableWalletUrl;
				}
			} else if (this.transaction.isOutgoing() && isOutgoingWallet) {
				walletUrl = this.isFiatTransaction() ? fiatWalletUrl : tradableWalletUrl;
			}
			this.router.navigateByUrl(walletUrl);
		}
	}

	public goToDividendAsset(): void {
		if (!this.isCashPlus) {
			this.closeEmitter.emit(true);
			let assetUrl = "";
			assetUrl = this.tradableListService
				.getItemById(this.transaction.getDividendAssetId())
				.getSymbol()
				.toLowerCase();
			setTimeout(() => this.router.navigateByUrl("/price/" + assetUrl), 300);
		}
	}

	public isClickable(): boolean {
		return !this.isFiatWithdrawal() && !this.isBestFeeTx() && this.transaction.getRecipient() !== "Bitpanda";
	}

	public isFiatWithdrawal(): boolean {
		return this.isFiatTransaction() && this.transaction.getType() === "withdrawal";
	}

	public isFiatDeposit(): boolean {
		return this.isFiatTransaction() && this.transaction.getType() === "deposit";
	}

	public getSmallBoxCss(): string {
		return this.isFiatDebitCardTx() ? "mod-flex-33" : "mod-flex-25";
	}

	public getLargeBoxCss(): string {
		return this.isFiatDebitCardTx() ? "mod-flex-67" : "mod-flex-75";
	}

	// This helper method checks if any values are provided for the properties used for rendering
	public hasMerchantDetails(transaction: Transaction): boolean {
		const merchantDetails = transaction?.getMerchantDetails();

		if (empty(merchantDetails)) {
			return false;
		}

		return (
			merchantDetails.getLocation() !== "" ||
			merchantDetails.getStreet() !== "" ||
			merchantDetails.getCity() !== ""
		);
	}

	private canOpenWallet(isOutgoingWallet: boolean): boolean {
		if (this.isDeletedWallet(isOutgoingWallet)) {
			return true;
		}

		// All edgecases that shouldn't be clickable for "sender" - wallets
		if (isOutgoingWallet) {
			return (
				!!this.getGiveawayReward() ||
				this.isFiatDeposit() ||
				this.isVoucherTx() ||
				this.transaction.isRefund() ||
				this.isExternalCryptoDepositWithFees() ||
				this.isOldGiveaway() ||
				this.transaction.getFrom() === "Bitpanda"
			);
		}

		// All edgecases that shouldn't be clickable for "Recipient" - wallets
		if (
			(!this.transaction.hasTrade() &&
				this.transaction.getType() !== "transfer" &&
				!this.transaction.isRefund() &&
				!this.isInternal &&
				this.transaction.getType() !== "deposit") ||
			this.transaction.getRecipient() === "Bitpanda"
		) {
			return true;
		}
		if (this.transaction.isTransfer() && this.transaction.isOutgoing() && this.isFiatTransaction()) {
			// Outgoing fiat transfer -> special edgecase for exchange
			return true;
		}
		if (this.isBestFeeTx()) {
			return true;
		}
	}

	// Since MerchantDetails object is always initialized with empty values,

	private openContacts(): void {
		setTimeout(() => {
			this.router.navigateByUrl("contacts");
			let contact: Contact = this.contactService.findByRecipient(this.transaction.getRecipient());
			if ((!contact || contact.isDeleted()) && isEmail(this.transaction.getRecipient())) {
				contact = new Contact();
				contact._email = this.transaction.getRecipient();
				this.modalService.showModal(ContactEditComponent, contact);
			}
		}, 300);
	}

	// Detect if the transaction is from CashPlus, use for Dividends CashPlus
	public isCashPlusAsset(): boolean {
		const assetGroupName = this.tradableListService
			.getItemById(this.transaction?.getDividendAssetId())
			?.getAssetGroupName();
		return assetGroupName === AssetGroupName.FiatEarn;
	}

	public onShareTrxClicked(): void {
		const trxInfo: DepositCellEvent = {
			walletId: this.transaction.getWalletId(),
			transactionPid: this.transaction.getId()
		};
		this.transactionTaxesFacade.onShareTrxClicked(trxInfo);

		// Segment Tracking
		this.taxesTrackerFacade.tracker(TAXES_TRX_DETAIL_PROMPT_CLICKED);
	}

	public isFiatTransaction(): boolean {
		return this.transaction instanceof FiatTransaction;
	}

	private isCryptoTransaction(): boolean {
		return this.transaction instanceof CryptoTransaction;
	}

	private handleNewTransactionStatus(): void {
		this.transactionTaxesFacade
			.getTransactionInfo(this.transaction.getId())
			.pipe(
				takeUntilDestroyed(this.destroyRef),
				finalize(() => this.isLoading$.next(false))
			)
			.subscribe((transactionInfo: TransactionInfo) => {
				const destination_tag_type: string =
					this.transaction.getCryptoCoinSymbol().toLocaleLowerCase() + "_destination_tag";
				const destination_tag_value: string = this.transaction.getDestinationTagValue();
				this.shouldDisplayTo =
					transactionInfo.payment_data.user_owned || !!transactionInfo.payment_data.receiver_legal_name;
				this.fromField = transactionInfo.payment_data.sender_legal_name;
				const transactionStatus: TransactionInfoStatus = transactionInfo.payment_data.transaction_status;
				this.displayRejectedInfoTooltip =
					this.isCryptoTransaction() && transactionStatus === TransactionInfoStatus.REJECTED;
				let status: string = this.translationService.translate(
					statusTranslationKeyMapping.get(transactionStatus)
				);
				if (transactionStatus === TransactionInfoStatus.PENDING && transactionInfo.flow === "outgoing") {
					status = this.translationService.translate("in_review");
				}
				const color: string = statusColorMapping.get(transactionStatus) ?? "";
				const wallet_host: string = transactionInfo.payment_data.wallet_host
					? transactionInfo.payment_data.wallet_host_name
						? transactionInfo.payment_data.wallet_host_name
						: this.translationService.translate("self_hosted_wallet")
					: "";
				const to: string = this.shouldDisplayTo
					? transactionInfo.payment_data.user_owned
						? this.profileName
						: transactionInfo.payment_data.receiver_legal_name
					: null;
				const walletAddress: string = transactionInfo.payment_data.wallet_address;
				this.transactionInfoStatus = {
					status,
					color,
					wallet_host,
					to,
					destination_tag_type,
					destination_tag_value,
					walletAddress
				};
			});
	}

	/**
	 * Calculates the unit price for merger crypto transactions
	 * Uses publicly accessible data from the transaction object
	 */
	public calculateMergerUnitPrice(): number {
		if (this.transaction instanceof CryptoTransaction) {
			const fiatAmount = +this.transaction.getCurrentFiatAmount();
			const cryptoAmount = +this.transaction.getAmount() || 0;

			if (cryptoAmount !== 0 && fiatAmount > 0) {
				return fiatAmount / cryptoAmount;
			}
		}
		return 0;
	}
}
